import { Box } from '@mui/material';
import { lazy, Suspense, useEffect, useMemo } from 'react';
import type {
  CohortHistoryChartConfig,
  DataPoint,
} from '@/features/chartsView';
import {
  LineChartWrapper,
  TABLEAU_20_COLORS,
  isChronologicalSplit,
  resolveTooltipMetrics,
  useLineChartParams,
} from '@/features/chartsView';
import { useDataFetcher } from '@/shared/hooks';
import type { DataStatus } from '@/shared/lib';
import { useAppSelector } from '@/shared/model';
import { isDateDimension, isMediaDimension } from '@/shared/types';
import { Loader } from '@/shared/ui';
import { CHART_BASE_DIMENSION } from '../../model/constants';
import { formatSnapshotDttm } from '../../model/formatSnapshotDttm';
import {
  hasVisibleCohortData,
  narrowToVisibleSnapshots,
  resolveCohortAnchor,
  resolveOpenCohorts,
  resolveSelectedCohortCutoffs,
  resolveSplitCohortCutoffs,
  trimImmatureCohorts,
} from '../../model/resolveCohortCutoff';
import { selectMeta } from '../../model/slice';
import type { CohortHistoryChartDataResponse } from '../../model/types';

const SeriesChart = lazy(() =>
  import('@/features/chartsView').then(({ SeriesChart }) => {
    return {
      default: SeriesChart<typeof CHART_BASE_DIMENSION>,
    };
  }),
);

const IMMATURE_COHORT_MESSAGE =
  'Every selected cohort is still filling up, so there are no complete snapshots yet. Check an earlier cohort to see its data.';

interface Props {
  config: CohortHistoryChartConfig;
  optionColors?: ReadonlyMap<string, string>;
  onGetData: () => Promise<CohortHistoryChartDataResponse>;
  onDataChange?: (data?: CohortHistoryChartDataResponse) => void;
  onDataStatusChange?: (dataStatus: DataStatus) => void;
  onOpenCohortsChange?: (options: string[]) => void;
}

export function Chart({
  config,
  optionColors,
  onGetData,
  onDataChange,
  onDataStatusChange,
  onOpenCohortsChange,
}: Props) {
  const historyMeta = useAppSelector(selectMeta);
  const isMediaContent = historyMeta
    ? isMediaDimension(historyMeta.config, config.splitDimension)
    : false;
  const isDateContent = historyMeta
    ? isDateDimension(historyMeta.config, config.splitDimension)
    : false;
  const { data, dataStatus } = useDataFetcher(onGetData, {
    keepPreviousData: true,
  });
  const splitOptions = useMemo(
    () => data?.splitOptions?.[config.splitDimension] ?? [],
    [data, config.splitDimension],
  );
  const cohortRangeEnd = useMemo(
    () => resolveCohortAnchor(config.cohortDateRange),
    [config.cohortDateRange],
  );
  const cutoffParams = useMemo(
    () => ({
      enabled: config.showOnlyFullCohorts,
      splitDimension: config.splitDimension,
      isChronological: isChronologicalSplit(historyMeta, config.splitDimension),
      splitOptions,
      cohortRangeEnd,
    }),
    [
      splitOptions,
      historyMeta,
      config.showOnlyFullCohorts,
      config.splitDimension,
      cohortRangeEnd,
    ],
  );
  const allCutoffs = useMemo(
    () => resolveSplitCohortCutoffs(cutoffParams),
    [cutoffParams],
  );
  const cutoffs = useMemo(
    () =>
      resolveSelectedCohortCutoffs({
        ...cutoffParams,
        excludedSplitOptions: config.excludedSplitOptions,
      }),
    [cutoffParams, config.excludedSplitOptions],
  );
  const openCohorts = useMemo(
    () =>
      resolveOpenCohorts(
        (data?.data || []) as DataPoint<typeof CHART_BASE_DIMENSION>[],
        CHART_BASE_DIMENSION,
        allCutoffs,
      ),
    [data, allCutoffs],
  );
  const hiddenLines = useMemo(
    () => [...new Set([...config.excludedSplitOptions, ...openCohorts])],
    [config.excludedSplitOptions, openCohorts],
  );
  // Trimmed before useLineChartParams so Y domains and noData reflect only the visible window.
  const chartSource = useMemo(
    () =>
      narrowToVisibleSnapshots(
        trimImmatureCohorts(
          (data?.data || []) as DataPoint<typeof CHART_BASE_DIMENSION>[],
          CHART_BASE_DIMENSION,
          cutoffs,
        ),
        config.excludedSplitOptions,
      ),
    [data, cutoffs, config.excludedSplitOptions],
  );
  const {
    noData,
    lines,
    leftY,
    rightY,
    tooltipYs,
    data: chartData,
  } = useLineChartParams({
    meta: historyMeta,
    chart: config,
    data: chartSource,
    splitOptions,
    defaultSplitOption: CHART_BASE_DIMENSION,
    excludedSplitOptions: hiddenLines,
    tooltipMetrics: resolveTooltipMetrics(config),
  });

  useEffect(() => {
    onDataChange?.(data);
  }, [data, onDataChange]);

  useEffect(() => {
    onOpenCohortsChange?.(openCohorts);
  }, [openCohorts, onOpenCohortsChange]);

  useEffect(() => {
    onDataStatusChange?.(dataStatus);
  }, [dataStatus, onDataStatusChange]);

  const noSelection = lines.length === config.excludedSplitOptions.length;
  const everyCohortImmature =
    cutoffs.size > 0 &&
    !!data?.data.length &&
    !hasVisibleCohortData(chartSource, config.excludedSplitOptions);
  const hasCompleteChart =
    !!data &&
    !noData &&
    !everyCohortImmature &&
    !leftY?.noData &&
    !rightY?.noData &&
    !noSelection;

  return (
    <Box sx={{ flexGrow: 1 }}>
      <LineChartWrapper
        dataStatus={dataStatus}
        keepContentWhileLoading={hasCompleteChart}
        noData={noData || everyCohortImmature}
        noDataMessage={
          everyCohortImmature ? IMMATURE_COHORT_MESSAGE : undefined
        }
        noSelection={noSelection}
      >
        <Suspense fallback={<Loader active />}>
          <SeriesChart
            sx={{ border: 'none' }}
            syncYAxesDomains={config.syncedYAxes}
            excludedLines={hiddenLines}
            highlightOnHover={config.highlightLineOnHover}
            isMediaContent={isMediaContent}
            isDateContent={isDateContent}
            xAxisDataKey={CHART_BASE_DIMENSION}
            xAxisFormatter={formatSnapshotDttm}
            leftY={leftY}
            rightY={rightY}
            tooltipYs={tooltipYs}
            data={chartData}
            lines={lines}
            leftSeries={config.leftAxisSeries}
            rightSeries={config.rightAxisSeries}
            palette={TABLEAU_20_COLORS}
            optionColors={optionColors}
          />
        </Suspense>
      </LineChartWrapper>
    </Box>
  );
}
